import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import aspectRatios, { AspectRatioIcon } from '../../constants/aspectRatios';
import resolutionMap, { RESOLUTION_OPTIONS } from '../../constants/resolutionMap';
import ModeSelector from './components/ModeSelector';
import CADZoneTypeSelector from './components/CADZoneTypeSelector';
import ReorganizationOptions from './components/ReorganizationOptions';
import UploadedImageThumb from '../../components/UploadedImageThumb';
import { useProjects } from '../../contexts/ProjectContext';
import toast, { Toaster } from 'react-hot-toast';
import AssetLibraryPanel from '../../components/AssetLibraryPanel';
import AngleViewDropdown from '../../components/AngleViewDropdown';
import RoomTypeDropdown from '../../components/RoomTypeDropdown';
import ModelSelectionDropdown from '../../components/ModelSelectionDropdown';
import { buildCADSpacePlannerInstruction, generateCADSpacePlanner, detectCADZones, shouldChargeForError, getErrorMessage } from '../../services/geminiService';
import { generateWithFlux, isFluxModel } from '../../services/fluxService';
import { useAuth } from '../../contexts/AuthContext';
import {
  projectsService,
  savedDesignsService,
  generationMetadataService,
  userProfilesService,
  storageService,
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import GenerationStatusNotification from '../../components/GenerationStatusNotification';
import useGenerationTimeout from '../../hooks/useGenerationTimeout';
import { getModelCreditCost } from '../../utils/creditCost';
import { checkSaveDesignLimit } from '../../utils/planLimits';

const CADSpacePlanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSaveModal } = useProjects();
  const { user, userProfile, loading, profileLoading } = useAuth();
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [cadImages, setCadImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [sessionLibraryImages, setSessionLibraryImages] = useState([]);

  // Mode selection state
  const [selectedMode, setSelectedMode] = useState('whole-room');
  const [modeError, setModeError] = useState(false);

  // CAD zone type toggle state
  const [cadZoneType, setCadZoneType] = useState(null);
  const [cadZoneTypeError, setCadZoneTypeError] = useState(false);

  // Selected CAD zone (specific-room only)
  const [selectedZone, setSelectedZone] = useState(null);

  // Reorganization options (whole-room only)
  const [zoneSwap, setZoneSwap] = useState(false);
  const [wallBorders, setWallBorders] = useState(false);

  // Control states
  const [selectedModel, setSelectedModel] = useState('Gemini 3.1 Flash Image Preview');
  const [selectedRoomType, setSelectedRoomType] = useState('--');
  const [selectedAngleView, setSelectedAngleView] = useState('--');
  const [selectedImageSize, setSelectedImageSize] = useState('Auto');
  const [isAspectRatioOpen, setIsAspectRatioOpen] = useState(false);
  const [selectedResolution, setSelectedResolution] = useState('1K');
  const [isResolutionOpen, setIsResolutionOpen] = useState(false);
  const aspectRatioRef = useRef(null);
  const aspectRatioMobileRef = useRef(null);
  const resolutionRef = useRef(null);
  const resolutionMobileRef = useRef(null);
  const [promptText, setPromptText] = useState('');

  // Virtual Tool Guide
  const [showGuide, setShowGuide] = useState(false);
  const [guideType, setGuideType] = useState('whole-room'); // 'whole-room' | 'specific-room'
  const [guideStep, setGuideStep] = useState(1);
  const [guideAnimating, setGuideAnimating] = useState(false);
  const [guideVisible, setGuideVisible] = useState(false);
  const totalGuideSteps = guideType === 'whole-room' ? 8 : 9;
  const angleViewGuideStep = guideType === 'whole-room' ? 5 : 6;
  const modelGuideStep = guideType === 'whole-room' ? 6 : 7;
  const promptGuideStep = guideType === 'whole-room' ? 7 : 8;
  const generateGuideStep = guideType === 'whole-room' ? 8 : 9;
  // Guide refs — desktop
  const modeSelectorDesktopRef = useRef(null);
  const cadZoneTypeDesktopRef = useRef(null);
  const uploadCadDesktopRef = useRef(null);
  const reorgOptionsDesktopRef = useRef(null);
  const cadZonePickerDesktopRef = useRef(null);
  const assetLibraryCadDesktopRef = useRef(null);
  const angleViewCadDesktopRef = useRef(null);
  const modelCadDesktopRef = useRef(null);
  const promptCadDesktopRef = useRef(null);
  const generateCadDesktopRef = useRef(null);
  // Guide refs — mobile
  const modeSelectorMobileRef = useRef(null);
  const cadZoneTypeMobileRef = useRef(null);
  const uploadCadMobileRef = useRef(null);
  const reorgOptionsMobileRef = useRef(null);
  const cadZonePickerMobileRef = useRef(null);
  const assetLibraryCadMobileRef = useRef(null);
  const angleViewCadMobileRef = useRef(null);
  const modelCadMobileRef = useRef(null);
  const promptCadMobileRef = useRef(null);
  const generateCadMobileRef = useRef(null);
  const [commonAngleView, setCommonAngleView] = useState('frontal');

  // Sync toggle → dropdown
  const handleToggleAngleView = () => {
    if (commonAngleView === 'frontal') {
      setCommonAngleView('topdown');
      setSelectedAngleView('Top-Down Flat View (90°)');
    } else {
      setCommonAngleView('frontal');
      setSelectedAngleView('Straight-On Eye-Level View');
    }
  };

  // Sync dropdown → toggle
  const handleAngleViewChange = (val) => {
    setSelectedAngleView(val);
    if (val === 'Straight-On Eye-Level View') {
      setCommonAngleView('frontal');
    } else if (val === 'Top-Down Flat View (90°)') {
      setCommonAngleView('topdown');
    } else {
      setCommonAngleView(null);
    }
  };
  const promptRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isGeneratingRef = useRef(false);

  // Zone detection states
  const [detectedZones, setDetectedZones] = useState([]);
  const [generatedZoneImages, setGeneratedZoneImages] = useState([]);
  const [isDetectingZones, setIsDetectingZones] = useState(false);

  // Generation status notification
  const { generationStatus, startTimer, clearTimer, setFailed, setCancelled, dismissNotification } = useGenerationTimeout();

  // Current project for saving generations
  const [currentProjectId, setCurrentProjectId] = useState(null);

  const roomTypes = ['--', 'Bedroom', 'Living room', 'Meeting room', 'Bathroom', 'Cabinet', 'Kitchen', 'Reception', 'Lounge', 'Bar', 'Terrace', 'Co-working area', 'Restaurant'];

  const imageSizes = ['Auto', '1:1', '4:3', '16:9', '21:9', '5:4', '3:2', '2:3', '9:16', '3:4', '4:5'];

  const MAX_TOTAL_IMAGES = 1;
  const totalUploadedImages = cadImages?.length || 0;
  const remainingImageSlots = Math.max(0, MAX_TOTAL_IMAGES - totalUploadedImages);

  const handleCadUpload = async (e) => {
    const files = Array.from(e?.target?.files);
    if (remainingImageSlots === 0) return;
    const limitedFiles = files.slice(0, remainingImageSlots);
    const imageUrls = (await Promise.all(limitedFiles.map((file) => storageService.uploadFile(file, user?.id)))).filter(Boolean);
    const updatedImages = [...cadImages, ...imageUrls];
    setCadImages(updatedImages);

    // Trigger zone detection if we have cadZoneType selected
    if (cadZoneType && updatedImages.length > 0) {
      detectZones(updatedImages, cadZoneType);
    }
  };

  // Zone detection function
  const detectZones = async (cadImageArray, zoneType) => {
    try {
      setIsDetectingZones(true);
      const zones = await detectCADZones({
        cadImages: cadImageArray,
        cadZoneType: zoneType,
        signal: abortControllerRef.current?.signal
      });

      setDetectedZones(zones);
      setGeneratedZoneImages(zones.map(zone => zone.image));
      console.log('Detected zones:', zones);
    } catch (error) {
      console.error('Zone detection error:', error);
      if (error.message?.includes('cancelled')) {
        return;
      }
      alert(`Zone detection failed: ${error.message}`);
    } finally {
      setIsDetectingZones(false);
    }
  };

  const handleRemoveCadImage = (idx) => {
    const imageToRemove = cadImages[idx];
    if (imageToRemove?.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove);
    }
    setCadImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    let hasError = false;
    if (!selectedMode) {
      setModeError(true);
      toast.error('Please select a mode before generating.');
      hasError = true;
    }
    if (!cadZoneType) {
      setCadZoneTypeError(true);
      toast.error('Please select a CAD Zone Type before generating.');
      hasError = true;
    }
    if (cadImages.length === 0) {
      toast.error('Please upload a CAD floor plan image.');
      hasError = true;
    }
    if (selectedMode === 'specific-room' && selectedZone === null) {
      toast.error('Please select a specific room zone to re-plan.');
      hasError = true;
    }
    if (hasError) {
      isGeneratingRef.current = false;
      return;
    }
    setModeError(false);
    setCadZoneTypeError(false);

    // Auth check
    const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
    if (!session || !session?.user?.id) {
      toast.error('Please sign in to generate images.');
      isGeneratingRef.current = false;
      return;
    }
    const currentUser = session?.user;

    // Credit check (supports team members sharing owner's pool)
    const creditCheck = await userProfilesService?.checkCredits(currentUser?.id, getModelCreditCost(selectedModel));
    if (!creditCheck?.allowed) {
      toast.error(creditCheck?.message || 'Insufficient credits.');
      isGeneratingRef.current = false;
      return;
    }

    // Check storage limit
    {
      const { allowed, current, max } = await checkSaveDesignLimit(currentUser?.id, userProfile?.subscription_tier);
      if (!allowed) {
        toast.error(`Storage limit reached (${current}/${max}). Please upgrade your plan or delete existing files.`);
        isGeneratingRef.current = false;
        return;
      }
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsGenerating(true);
      setGeneratedImage(null);
      startTimer();
      setGeneratedImage(null);

      const dimensions = getResolutionDimensions(selectedResolution);

      let generatedImageUrl;

      if (isFluxModel(selectedModel)) {
        const sharedPrompt = buildCADSpacePlannerInstruction({
          cadZoneType,
          selectedMode,
          zoneSwap,
          wallBorders,
          promptText,
          selectedRoomType,
          commonAngleView,
          selectedAngleView,
        });

        generatedImageUrl = await generateWithFlux({
          modelName: selectedModel,
          directPromptText: sharedPrompt,
          userPromptText: promptText,
          referenceImages: cadImages,
          roomType: selectedRoomType === 'Custom' ? '' : selectedRoomType,
          selectedAngleShot: selectedAngleView,
          imageSize: selectedImageSize,
          signal: controller.signal,
        });
      } else {
        // Gemini model: generates single result
        const result = await generateCADSpacePlanner({
          cadImages,
          cadZoneType,
          selectedMode,
          selectedZone,
          generatedZoneImages,
          zoneSwap,
          wallBorders,
          promptText,
          selectedRoomType,
          commonAngleView,
          selectedAngleView,
          selectedImageSize,
          dimensions,
          selectedModel,
          signal: controller.signal,
        });
        generatedImageUrl = result?.optionA || result?.optionB;
      }

      if (generatedImageUrl) {
        setGeneratedImage(generatedImageUrl);
        setLibraryAssets((prev) => [...prev, generatedImageUrl]);
        setSessionLibraryImages((prev) => [...prev, generatedImageUrl]);
      }

      clearTimer();
      toast.success('Image generated successfully!');
      const displayImage = generatedImageUrl;
      if (displayImage) {
        showSaveModal(displayImage, location.pathname);
        // Save to database
        await saveGenerationToDatabase(displayImage, promptText || '', currentUser);
        // Deduct credits only if image was actually generated
        if (generatedImageUrl) {
          await userProfilesService?.updateCredits(currentUser?.id, getModelCreditCost(selectedModel));
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError' || error?.message?.includes('cancelled')) {
        setCancelled();
      } else {
        setFailed();
        console.error('Generation error:', error);
      }
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
      isGeneratingRef.current = false;
    }
  };

  const saveGenerationToDatabase = async (imageUrl, prompt, currentUser = null) => {
    const activeUser = currentUser || user;
    if (!activeUser) return;

    try {
      // Upload image to Supabase Storage (convert base64 → public URL)
      const storageUrl = await storageService.uploadImage(imageUrl, activeUser?.id);
      imageUrl = storageUrl;

      let projectId = currentProjectId;
      if (!projectId) {
        const projectName = `${selectedRoomType || 'CAD Space Plan'} - ${new Date()?.toLocaleDateString()}`;
        const { data: project, error: projectError } = await projectsService?.createProject(activeUser?.id, {
          name: projectName,
          description: prompt,
          thumbnail: imageUrl,
          toolType: 'cad-space-planner',
          toolName: 'CAD Space Planner',
          status: 'completed',
          tags: [selectedRoomType, cadZoneType, selectedMode, selectedModel]?.filter(Boolean),
          variations: 1,
        });
        if (projectError) { console.error('Error creating project:', projectError); return; }
        projectId = project?.id;
        setCurrentProjectId(projectId);
      } else {
        await projectsService?.incrementVariations(projectId);
        await projectsService?.updateProject(projectId, { thumbnail: imageUrl });
      }

      const { data: design, error: designError } = await savedDesignsService?.createDesign(activeUser?.id, projectId, {
        imageUrl,
        prompt,
        isFavorite: false,
      });
      if (designError) { console.error('Error saving design:', designError); return; }

      await generationMetadataService?.createMetadata(activeUser?.id, design?.id, {
        modelUsed: selectedModel,
        imageSize: selectedImageSize,
        roomType: (selectedRoomType === 'Custom' ? '' : selectedRoomType) || 'Room',
        angleView: selectedAngleView || '--',
        style: `CAD Space Planner - ${selectedMode || 'Default'}`,
        resolution: selectedImageSize,
        cameraView: selectedAngleView || '--',
        lighting: 'Auto',
        quality: 'High',
        usedMedia: [...cadImages, imageUrl],
        additionalParams: {
          prompt: promptText,
          selectedMode,
          selectedZone,
          zoneSwap,
          wallBorders,
          cadZoneType,
        },
      });
    } catch (error) {
      console.error('Error saving to database:', error);
    }
  };

  const handleCancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setCancelled();
      setIsGenerating(false);
    }
  };

  const handleDownload = (imageUrl, label) => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.download = `space-planner-${label}-${timestamp}.png`;
    a.click();
  };

  // Restore project data when opened from /project-history
  useEffect(() => {
    const projectId = location.state?.projectId;
    if (!projectId) return;
    const loadProjectData = async () => {
      setCurrentProjectId(projectId);
      const designs = await savedDesignsService.getDesigns(projectId);
      if (designs && designs.length > 0) {
        const imageUrls = designs.map((d) => d.imageUrl).filter(Boolean);
        if (imageUrls.length > 0) setSessionLibraryImages(imageUrls);
        setGeneratedImage(designs[0]?.imageUrl || null);
        // Restore generation settings from metadata
        const metadata = await generationMetadataService.getMetadata(designs[0].id);
        if (metadata) {
          if (metadata.modelUsed) setSelectedModel(metadata.modelUsed);
          if (metadata.imageSize) setSelectedImageSize(metadata.imageSize);
          if (metadata.roomType && metadata.roomType !== 'Room') setSelectedRoomType(metadata.roomType);
          if (metadata.angleView && metadata.angleView !== '--') setSelectedAngleView(metadata.angleView);
          if (metadata?.usedMedia?.length > 0) setLibraryAssets(metadata.usedMedia);
          if (metadata?.additionalParams?.prompt) setPromptText(metadata.additionalParams.prompt);
          if (metadata?.additionalParams?.selectedMode) setSelectedMode(metadata.additionalParams.selectedMode);
          if (metadata?.additionalParams?.selectedZone) setSelectedZone(metadata.additionalParams.selectedZone);
          if (metadata?.additionalParams?.zoneSwap != null) setZoneSwap(metadata.additionalParams.zoneSwap);
          if (metadata?.additionalParams?.wallBorders != null) setWallBorders(metadata.additionalParams.wallBorders);
          if (metadata?.additionalParams?.cadZoneType) setCadZoneType(metadata.additionalParams.cadZoneType);
        }
        if (designs[0]?.prompt && !promptText) setPromptText(designs[0].prompt);
      }
    };
    loadProjectData();
  }, []);

  // Close aspect ratio dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inDesktopAR = aspectRatioRef?.current && aspectRatioRef.current.contains(event?.target);
      const inMobileAR = aspectRatioMobileRef?.current && aspectRatioMobileRef.current.contains(event?.target);
      if (!inDesktopAR && !inMobileAR) {
        setIsAspectRatioOpen(false);
      }
      const inDesktopRes = resolutionRef?.current && resolutionRef.current.contains(event?.target);
      const inMobileRes = resolutionMobileRef?.current && resolutionMobileRef.current.contains(event?.target);
      if (!inDesktopRes && !inMobileRes) {
        setIsResolutionOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getResolutionDimensions = (resKey) => {
    const ratioMap = resolutionMap[selectedImageSize];
    if (ratioMap && ratioMap[resKey]) {
      return ratioMap[resKey];
    }
    return resolutionMap['1:1'][resKey] || { width: 1024, height: 1024 };
  };

  // Auto-resize textarea
  useEffect(() => {
    if (promptRef?.current) {
      promptRef.current.style.height = 'auto';
      promptRef.current.style.height = promptRef?.current?.scrollHeight + 'px';
    }
  }, [promptText]);

  const glow = '0 0 32px rgba(180,120,60,0.5), 0 0 80px rgba(180,120,60,0.35), 0 0 150px rgba(180,120,60,0.2)';

  const openGuide = (type) => {
    setGuideType(type);
    setSelectedMode(type);
    setModeError(false);
    setGuideStep(1);
    setShowGuide(true);
    setGuideVisible(false);
    const ref = window.innerWidth >= 768 ? modeSelectorDesktopRef.current : modeSelectorMobileRef.current;
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }, 500);
    } else {
      requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
    }
  };

  const nextGuideStep = () => {
    if (guideAnimating) return;
    if (guideStep >= totalGuideSteps) { closeGuide(); return; }
    setGuideAnimating(true);
    setGuideVisible(false);
    const nextStep = guideStep + 1;
    const wholeRoomDesktopRefs = [modeSelectorDesktopRef, cadZoneTypeDesktopRef, uploadCadDesktopRef, reorgOptionsDesktopRef, angleViewCadDesktopRef, modelCadDesktopRef, promptCadDesktopRef, generateCadDesktopRef];
    const specificRoomDesktopRefs = [modeSelectorDesktopRef, cadZoneTypeDesktopRef, uploadCadDesktopRef, assetLibraryCadDesktopRef, cadZonePickerDesktopRef, angleViewCadDesktopRef, modelCadDesktopRef, promptCadDesktopRef, generateCadDesktopRef];
    const wholeRoomMobileRefs = [modeSelectorMobileRef, cadZoneTypeMobileRef, uploadCadMobileRef, reorgOptionsMobileRef, angleViewCadMobileRef, modelCadMobileRef, promptCadMobileRef, generateCadMobileRef];
    const specificRoomMobileRefs = [modeSelectorMobileRef, cadZoneTypeMobileRef, uploadCadMobileRef, assetLibraryCadMobileRef, cadZonePickerMobileRef, angleViewCadMobileRef, modelCadMobileRef, promptCadMobileRef, generateCadMobileRef];
    const desktopRefs = guideType === 'whole-room' ? wholeRoomDesktopRefs : specificRoomDesktopRefs;
    const mobileRefs = guideType === 'whole-room' ? wholeRoomMobileRefs : specificRoomMobileRefs;
    const ref = window.innerWidth >= 768 ? desktopRefs[nextStep - 1]?.current : mobileRefs[nextStep - 1]?.current;
    if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      setGuideStep(nextStep);
      setGuideAnimating(false);
      setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => setGuideVisible(true)));
      }, 400);
    }, 600);
  };

  const closeGuide = () => {
    setGuideVisible(false);
    setTimeout(() => {
      setShowGuide(false);
      setGuideStep(1);
      setGuideAnimating(false);
    }, 600);
  };

  return (
    <>
      <GenerationStatusNotification status={generationStatus} onDismiss={dismissNotification} />
      <Helmet>
        <title>Space Planner - Roomwork.ai</title>
        <meta
          name="description"
          content="Transform CAD drawings and mood images into stunning 3D interior visualizations with AI-powered design generation" />
      </Helmet>
      <div className="min-h-screen bg-black overflow-x-hidden">
        <Header />
        <main className="pt-20 lg:pt-24 px-4 pb-[20vh]">
          <div className="max-w-[1400px] mx-auto">
            {/* Header Section */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="flex items-center space-x-2 text-white/70 hover:text-white transition-colors mb-4">
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>

              <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">Space Planner</h1>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => openGuide('whole-room')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
                    <Icon name="HelpCircle" size={18} />
                    <span className="hidden md:inline">Whole Room Guide</span>
                  </button>
                  <button
                    onClick={() => openGuide('specific-room')}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium hover:scale-[1.02] transition-all flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', boxShadow: '0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
                    <Icon name="HelpCircle" size={18} />
                    <span className="hidden md:inline">Specific Room Guide</span>
                  </button>
                  <button
                    onClick={() => navigate('/project-history')}
                    className="flex items-center gap-2 px-4 py-2 glassmorphic-button rounded-xl text-white text-sm hover:scale-105 transition-transform flex-shrink-0">
                    <Icon name="History" size={18} />
                    <span className="hidden md:inline">View Projects</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Mode Selector - Desktop */}
            <div className="hidden md:block mb-6">
              <div ref={modeSelectorDesktopRef} className="rounded-xl" style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <ModeSelector
                  selectedMode={selectedMode}
                  onModeChange={(mode) => { setSelectedMode(mode); setModeError(false); }}
                  showError={modeError}
                />
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex gap-6">
              {/* Left Column (1/3) - CAD Zone Type + Upload CAD Image */}
              <div className="w-[30%] flex-shrink-0 space-y-6">
                {/* CAD Zone Type Selector */}
                <div ref={cadZoneTypeDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                  <CADZoneTypeSelector
                    selectedType={cadZoneType}
                    onTypeChange={(type) => {
                      setCadZoneType(type);
                      setCadZoneTypeError(false);
                      // Trigger zone detection if we have CAD images
                      if (cadImages.length > 0) {
                        detectZones(cadImages, type);
                      }
                    }}
                    disabled={!selectedMode}
                    showError={cadZoneTypeError}
                  />
                </div>

                <div className="text-left mb-2">
                  <span className={`text-sm font-medium ${totalUploadedImages >= MAX_TOTAL_IMAGES ? "text-red-400" : "text-white/70"}`}>
                    {totalUploadedImages} / {MAX_TOTAL_IMAGES} images uploaded
                  </span>
                </div>
                <div ref={uploadCadDesktopRef} className="rounded-3xl" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <div
                  className="glassmorphic-card rounded-3xl p-6 border border-transparent transition-all duration-200"
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-500/50');
                    const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                    if (imageUrl) {
                      const totalCurrent = cadImages.length;
                      if (totalCurrent < MAX_TOTAL_IMAGES) {
                        setCadImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                      }
                      return;
                    }
                    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                    if (files.length > 0) {
                      const remaining = Math.max(0, MAX_TOTAL_IMAGES - cadImages.length);
                      const limited = files.slice(0, remaining);
                      const urls = (await Promise.all(limited.map(f => storageService.uploadFile(f, user?.id)))).filter(Boolean);
                      setCadImages(prev => [...prev, ...urls]);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[12.6px] font-medium text-white">
                      Upload CAD image<span className="text-red-500">*</span>
                    </h3>
                  </div>
                  <label className={`block ${remainingImageSlots === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="hidden"
                      disabled={remainingImageSlots === 0}
                      onChange={handleCadUpload} />
                    <div className={`glassmorphic-upload-zone rounded-2xl p-16 flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${remainingImageSlots === 0 ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                      <Icon name="ImagePlus" size={48} className={`mb-3 ${remainingImageSlots === 0 ? 'text-white/30' : 'text-orange-500'}`} />
                      <p className="text-white/70 text-sm">
                        {remainingImageSlots === 0 ? 'Maximum image reached' : 'Upload image PNG or JPG'}
                      </p>
                    </div>
                  </label>
                  {cadImages?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {cadImages?.map((img, idx) => (
                        <UploadedImageThumb key={idx} src={img} alt={`CAD ${idx + 1}`} onRemove={() => handleRemoveCadImage(idx)} imgClass="max-w-[80px] max-h-[60px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                      ))}
                    </div>
                  )}
                </div>
                </div>{/* end uploadCadDesktopRef */}

                {/* Reorganization Options - Whole Room only */}
                {selectedMode === 'whole-room' && (
                  <div ref={reorgOptionsDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 4 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    <ReorganizationOptions
                      zoneSwap={zoneSwap}
                      wallBorders={wallBorders}
                      onZoneSwapChange={setZoneSwap}
                      onWallBordersChange={setWallBorders}
                    />
                  </div>
                )}

                <div ref={assetLibraryCadDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 4 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                  <AssetLibraryPanel
                    libraryAssets={libraryAssets}
                    setLibraryAssets={setLibraryAssets}
                    generatedImages={sessionLibraryImages}
                    setGeneratedImages={setSessionLibraryImages}
                    currentProjectId={currentProjectId}
                    onImageDeleted={(remaining) => {
                      if (!isGenerating) setGeneratedImage(remaining[0] || null);
                    }}
                  />
                </div>
              </div>

              {/* Right Column (2/3) - Result + Controls */}
              <div className="w-[70%] flex-shrink-0 space-y-6">
                {/* Result Window - Single Output */}
                <div className="glassmorphic-card rounded-3xl p-4">
                  <div className="flex justify-end items-center mb-3 px-2">
                    <span className="text-white/70 text-sm">Result</span>
                  </div>
                  <div className="relative rounded-2xl overflow-hidden bg-black/40 min-h-[400px]">
                    {isGenerating ? (
                      <div className="w-full min-h-[400px] flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Loader" size={48} className="text-orange-500 mx-auto mb-3 animate-spin" />
                          <p className="text-white text-sm font-medium">Generating your space plan...</p>
                          <p className="text-white/60 text-xs mt-1">This may take a moment</p>
                        </div>
                      </div>
                    ) : generatedImage ? (
                      <img
                        src={generatedImage}
                        alt="Generated result"
                        className="w-full h-auto object-contain" />
                    ) : (
                      <div className="w-full min-h-[400px] flex items-center justify-center">
                        <div className="text-center">
                          <Icon name="Image" size={48} className="text-white/30 mx-auto mb-3" />
                          <p className="text-white/50 text-sm">No image generated yet</p>
                          <p className="text-white/30 text-xs mt-1">Upload images and click Generate</p>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="absolute bottom-4 right-4 flex gap-2">
                      <button
                        onClick={() => handleDownload(generatedImage, 'result')}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Download">
                        <Icon name="Download" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={() => { setGeneratedImage(null); }}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete">
                        <Icon name="Trash2" size={18} className="text-white" />
                      </button>
                      <button
                        onClick={handleGenerate}
                        disabled={!generatedImage || isGenerating}
                        className="p-2.5 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Regenerate">
                        <Icon name="RefreshCw" size={18} className="text-white" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Controls Section */}
                <div className="glassmorphic-card rounded-3xl p-6 space-y-4">
                  {/* Choose CAD zone - Specific Room only */}
                  {selectedMode === 'specific-room' && (
                    <div ref={cadZonePickerDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 5 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                      <div>
                        <span className="text-white/70 text-sm block mb-3">
                          Choose CAD zone you want to re-furnish<span className="text-red-500">*</span>
                        </span>

                        {isDetectingZones ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="text-center">
                              <Icon name="Loader" size={32} className="text-orange-500 mx-auto mb-3 animate-spin" />
                              <p className="text-white/70 text-sm">Detecting room zones...</p>
                            </div>
                          </div>
                        ) : generatedZoneImages?.length > 0 ? (
                          <div className="grid grid-cols-4 gap-2.5">
                            {generatedZoneImages?.map((img, idx) => {
                              const zone = detectedZones[idx];
                              const roomLabel = zone?.label || `Zone ${idx + 1}`;
                              const isSelected = selectedZone === idx;
                              const isFailed = zone?.failed === true;
                              return (
                                <div key={idx} className="relative">
                                  <button
                                    onClick={() => !isFailed && setSelectedZone(idx)}
                                    className={`w-full cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                                      isFailed
                                        ? 'border-red-500/30 opacity-50 cursor-not-allowed'
                                        : isSelected
                                          ? 'border-orange-500/60 shadow-[0_0_12px_rgba(254,131,56,0.15)]'
                                          : 'border-white/10 hover:border-orange-500/30'
                                    }`}
                                    disabled={isFailed}
                                    title={isFailed ? 'Zone detection failed for this room' : `Select ${roomLabel}`}>
                                    <img
                                      src={img}
                                      alt={roomLabel}
                                      className="w-full h-28 object-cover" />
                                    {isFailed && (
                                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                        <Icon name="AlertTriangle" size={20} className="text-red-400" />
                                      </div>
                                    )}
                                  </button>
                                  <span className={`text-xs mt-1 block text-center transition-colors ${
                                    isFailed ? 'text-red-400' : isSelected ? 'text-orange-400' : 'text-white/60'
                                  }`}>{roomLabel}{isFailed ? ' (failed)' : ''}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-white/50 text-sm">Upload CAD image and select zone type to detect rooms</p>
                          </div>
                        )}
                      </div>

                      {/* Separator */}
                      <div className="border-t border-white/20 my-4"></div>
                    </div>
                  )}

                  {selectedMode === 'specific-room' ? (
                    <>
                      {/* Specify CAD zone type + Angle view */}
                      <div ref={angleViewCadDesktopRef} className="grid grid-cols-2 gap-4 rounded-xl" style={{ position: showGuide && guideStep === 6 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                        <RoomTypeDropdown
                          value={selectedRoomType}
                          onChange={(val) => {
                            setSelectedRoomType(val);
                            if (val === 'Custom') {
                              setTimeout(() => {
                                if (promptRef?.current) {
                                  promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  promptRef.current.focus();
                                }
                              }, 80);
                            }
                          }}
                          label="Specify chosen CAD zone type"
                          showCustomOption
                        />
                        <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} />
                      </div>

                      {/* Common angle views (left) + Aspect Ratio (right) */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-white/70 text-xs mb-3 block">Common angle views</label>
                          <div className="flex items-center justify-start gap-2">
                            <span className={`text-xs transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>Frontal view</span>
                            <button
                              onClick={handleToggleAngleView}
                              className={`relative w-14 h-7 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                              <div
                                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                                  commonAngleView === 'topdown' ? 'translate-x-7' : 'translate-x-0'}`} />
                            </button>
                            <span className={`text-xs transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>Top-down view</span>
                          </div>
                        </div>
                        <div ref={aspectRatioRef}>
                          <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                          <div className="relative">
                            <button
                              onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                              className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all">
                              <div className="flex items-center gap-3">
                                <AspectRatioIcon
                                  ratio={selectedImageSize}
                                  size={18}
                                  className="text-orange-500" />
                                <span className="text-white text-sm">{selectedImageSize}</span>
                              </div>
                              <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                            </button>
                            {isAspectRatioOpen && (
                              <div
                                className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                                style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                                {aspectRatios?.map((ratio) => (
                                  <button
                                    key={ratio?.value}
                                    onClick={() => { setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                                    className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                      selectedImageSize === ratio?.value ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                    }`}>
                                    <div className="flex items-center gap-3">
                                      <AspectRatioIcon
                                        ratio={ratio?.value}
                                        size={18}
                                        className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'} />
                                      <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                        {ratio?.label}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Model Selection (left) + Resolution (right) */}
                      <div ref={modelCadDesktopRef} className="grid grid-cols-2 gap-4 rounded-xl" style={{ position: showGuide && guideStep === 7 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                        <ModelSelectionDropdown
                          value={selectedModel}
                          onChange={setSelectedModel}
                        />
                        <div ref={resolutionRef}>
                          {!isFluxModel(selectedModel) && (
                          <>
                          <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                          <div className="relative">
                            <button
                              onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                              className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}>
                              <span className="text-white text-sm font-medium">{selectedResolution}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-white/50 text-xs">
                                    {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                                  </span>
                                <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                              </div>
                            </button>
                            {isResolutionOpen && (
                              <div
                                className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                                style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                                {RESOLUTION_OPTIONS.map((resKey) => {
                                  const dims = getResolutionDimensions(resKey);
                                  return (
                                    <button
                                      key={resKey}
                                      onClick={() => { setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                                      className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                        selectedResolution === resKey ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                      }`}>
                                      <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                        {resKey}
                                      </span>
                                      <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                          {dims.width}×{dims.height}
                                        </span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          </>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Whole room: Common angle views + Angle view */}
                      <div ref={angleViewCadDesktopRef} className="grid grid-cols-2 gap-4 rounded-xl" style={{ position: showGuide && guideStep === 5 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                        <div>
                          <label className="text-white/70 text-xs mb-3 block">Common angle views</label>
                          <div className="flex items-center justify-start gap-2">
                            <span className={`text-xs transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>Frontal view</span>
                            <button
                              onClick={handleToggleAngleView}
                              className={`relative w-14 h-7 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                              <div
                                className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                                  commonAngleView === 'topdown' ? 'translate-x-7' : 'translate-x-0'}`} />
                            </button>
                            <span className={`text-xs transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>Top-down view</span>
                          </div>
                        </div>
                        <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} />
                      </div>

                      <div ref={modelCadDesktopRef} className="space-y-4 rounded-xl" style={{ position: showGuide && guideStep === 6 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                      {/* Model Selection (left) + Aspect Ratio (right) */}
                      <div className="grid grid-cols-2 gap-4">
                        <ModelSelectionDropdown
                          value={selectedModel}
                          onChange={setSelectedModel}
                        />
                        <div ref={aspectRatioRef}>
                          <label className="text-white/70 text-xs mb-2 block">Aspect Ratio</label>
                          <div className="relative">
                            <button
                              onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                              className="w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all">
                              <div className="flex items-center gap-3">
                                <AspectRatioIcon
                                  ratio={selectedImageSize}
                                  size={18}
                                  className="text-orange-500" />
                                <span className="text-white text-sm">{selectedImageSize}</span>
                              </div>
                              <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                            </button>
                            {isAspectRatioOpen && (
                              <div
                                className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                                style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                                {aspectRatios?.map((ratio) => (
                                  <button
                                    key={ratio?.value}
                                    onClick={() => { setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                                    className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                      selectedImageSize === ratio?.value ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                    }`}>
                                    <div className="flex items-center gap-3">
                                      <AspectRatioIcon
                                        ratio={ratio?.value}
                                        size={18}
                                        className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'} />
                                      <span className={`text-sm ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                        {ratio?.label}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                    {/* Resolution Selection */}
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div ref={resolutionRef}>
                        {!isFluxModel(selectedModel) && (
                        <>
                        <label className="text-white/70 text-xs mb-2 block">Resolution</label>
                        <div className="relative">
                          <button
                            onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                            className={`w-full glassmorphic-select rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}>
                            <span className="text-white text-sm font-medium">{selectedResolution}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-white/50 text-xs">
                                  {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                                </span>
                              <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-white/50" />
                            </div>
                          </button>
                          {isResolutionOpen && (
                            <div
                              className="absolute z-50 w-full bottom-full mb-2 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
                              style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                              {RESOLUTION_OPTIONS.map((resKey) => {
                                const dims = getResolutionDimensions(resKey);
                                return (
                                  <button
                                    key={resKey}
                                    onClick={() => { setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                                    className={`w-full flex items-center justify-between px-4 py-3 transition-all border-b border-white/5 last:border-b-0 ${
                                      selectedResolution === resKey ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                    }`}>
                                    <span className={`text-sm ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                      {resKey}
                                    </span>
                                    <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                        {dims.width}×{dims.height}
                                      </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        </>
                        )}
                      </div>
                    </div>
                    </div>
                  </>
                  )}

                  {/* Prompt */}
                  <div ref={promptCadDesktopRef} className="rounded-xl" style={{ position: showGuide && guideStep === promptGuideStep ? 'relative' : undefined, zIndex: showGuide && guideStep === promptGuideStep ? 201 : undefined, boxShadow: showGuide && guideStep === promptGuideStep && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    <label className="text-white/70 text-xs mb-2 block">Prompt:</label>
                    {selectedRoomType === 'Custom' && (
                      <p className="text-orange-400/80 text-[11px] mb-2 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                    )}
                    <textarea
                      ref={promptRef}
                      value={promptText}
                      onChange={(e) => setPromptText(e?.target?.value)}
                      placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio, reading nook, wine cellar...)' : 'Additional request'}
                      rows={5}
                      className={`w-full glassmorphic-input rounded-2xl px-4 py-3 text-white bg-white/5 focus:outline-none transition-all placeholder:text-white/30 resize-none overflow-y-auto min-h-[120px] max-h-[300px] ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`}
                      style={{ height: '120px' }}
                    />
                  </div>

                  {/* Generate Button */}
                  <div ref={generateCadDesktopRef} className="rounded-2xl" style={{ position: showGuide && guideStep === generateGuideStep ? 'relative' : undefined, zIndex: showGuide && guideStep === generateGuideStep ? 201 : undefined, boxShadow: showGuide && guideStep === generateGuideStep && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full glassmorphic-button rounded-2xl py-3 text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100">
                    <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={18} className={isGenerating ? 'animate-spin' : ''} />
                    {isGenerating ? 'Generating...' : 'Generate Image'}
                  </button>
                  {isGenerating && (
                    <button
                      onClick={handleCancelGeneration}
                      className="w-full glassmorphic-button rounded-2xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                    >
                      <Icon name="X" size={16} />
                      Cancel
                    </button>
                  )}
                  </div>

                  <p className="text-white/50 text-xs text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-4">
              {/* Mode Selector - Mobile */}
              <div ref={modeSelectorMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 1 ? 'relative' : undefined, zIndex: showGuide && guideStep === 1 ? 201 : undefined, boxShadow: showGuide && guideStep === 1 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <ModeSelector
                  selectedMode={selectedMode}
                  onModeChange={(mode) => { setSelectedMode(mode); setModeError(false); }}
                  showError={modeError}
                />
              </div>

              {/* Result Window - Mobile (Single Output) */}
              <div className="glassmorphic-card rounded-2xl p-3">
                <div className="flex justify-end items-center mb-2 px-1">
                  <span className="text-white/70 text-xs">Result</span>
                </div>
                <div className="relative rounded-xl overflow-hidden bg-black/40 min-h-[200px]">
                  {isGenerating ? (
                    <div className="w-full min-h-[200px] flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Loader" size={32} className="text-orange-500 mx-auto mb-2 animate-spin" />
                        <p className="text-white text-xs font-medium">Generating...</p>
                      </div>
                    </div>
                  ) : generatedImage ? (
                    <img
                      src={generatedImage}
                      alt="Generated result"
                      className="w-full h-auto object-contain" />
                  ) : (
                    <div className="w-full min-h-[200px] flex items-center justify-center">
                      <div className="text-center">
                        <Icon name="Image" size={32} className="text-white/30 mx-auto mb-2" />
                        <p className="text-white/50 text-xs">No image generated yet</p>
                      </div>
                    </div>
                  )}

                  <div className="absolute bottom-3 right-3 flex gap-1.5">
                    <button
                      onClick={() => handleDownload(generatedImage, 'result')}
                      disabled={!generatedImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download">
                      <Icon name="Download" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={() => { setGeneratedImage(null); }}
                      disabled={!generatedImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete">
                      <Icon name="Trash2" size={14} className="text-white" />
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={!generatedImage || isGenerating}
                      className="p-2 rounded-lg bg-black/70 hover:bg-black/80 border border-white/30 shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Regenerate">
                      <Icon name="RefreshCw" size={14} className="text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* CAD Zone Type Selector - Mobile */}
              <div ref={cadZoneTypeMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 2 ? 'relative' : undefined, zIndex: showGuide && guideStep === 2 ? 201 : undefined, boxShadow: showGuide && guideStep === 2 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <CADZoneTypeSelector
                  selectedType={cadZoneType}
                  onTypeChange={(type) => {
                    setCadZoneType(type);
                    setCadZoneTypeError(false);
                    // Trigger zone detection if we have CAD images
                    if (cadImages.length > 0) {
                      detectZones(cadImages, type);
                    }
                  }}
                  disabled={!selectedMode}
                  showError={cadZoneTypeError}
                />
              </div>

              {/* Upload CAD Image */}
              <div className="text-left mb-2">
                <span className={`text-sm font-medium ${totalUploadedImages >= MAX_TOTAL_IMAGES ? "text-red-400" : "text-white/70"}`}>
                  {totalUploadedImages} / {MAX_TOTAL_IMAGES} images uploaded
                </span>
              </div>
              <div ref={uploadCadMobileRef} className="rounded-3xl" style={{ position: showGuide && guideStep === 3 ? 'relative' : undefined, zIndex: showGuide && guideStep === 3 ? 201 : undefined, boxShadow: showGuide && guideStep === 3 && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
              <div
                className="glassmorphic-card rounded-3xl p-4 border border-transparent transition-all duration-200"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; e.currentTarget.classList.add('border-orange-500/50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-500/50'); }}
                onDrop={async (e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-orange-500/50');
                  const imageUrl = e.dataTransfer.getData('imageUrl') || e.dataTransfer.getData('text/plain');
                  if (imageUrl) {
                    const totalCurrent = cadImages.length;
                    if (totalCurrent < MAX_TOTAL_IMAGES) {
                      setCadImages((prev) => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
                    }
                    return;
                  }
                  const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
                  if (files.length > 0) {
                    const remaining = Math.max(0, MAX_TOTAL_IMAGES - cadImages.length);
                    const limited = files.slice(0, remaining);
                    const urls = (await Promise.all(limited.map(f => storageService.uploadFile(f, user?.id)))).filter(Boolean);
                    setCadImages(prev => [...prev, ...urls]);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white text-xs font-medium">
                    Upload CAD image<span className="text-red-500">*</span>
                  </h3>
                </div>
                <label className={`block ${remainingImageSlots === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    disabled={remainingImageSlots === 0}
                    onChange={handleCadUpload} />
                  <div className={`glassmorphic-upload-zone rounded-2xl h-[120px] flex flex-col items-center justify-center text-center border-2 border-dashed transition-all duration-300 ${remainingImageSlots === 0 ? 'border-white/10' : 'border-white/20 hover:border-orange-500/40'}`}>
                    <Icon name="ImagePlus" size={32} className={`mb-2 ${remainingImageSlots === 0 ? 'text-white/30' : 'text-orange-500'}`} />
                    <p className="text-white text-xs">
                      {remainingImageSlots === 0 ? 'Max reached' : 'Upload'}
                    </p>
                  </div>
                </label>
                {cadImages?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {cadImages?.map((img, idx) => (
                      <UploadedImageThumb key={idx} src={img} alt={`CAD ${idx + 1}`} onRemove={() => handleRemoveCadImage(idx)} imgClass="max-w-[60px] max-h-[45px] object-contain rounded-lg border border-white/10" height="h-auto" previewWidth="w-[200px]" />
                    ))}
                  </div>
                )}
              </div>
              </div>{/* end uploadCadMobileRef */}

              {/* Reorganization Options - Mobile, Whole Room only */}
              {selectedMode === 'whole-room' && (
                <div ref={reorgOptionsMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 4 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                  <ReorganizationOptions
                    zoneSwap={zoneSwap}
                    wallBorders={wallBorders}
                    onZoneSwapChange={setZoneSwap}
                    onWallBordersChange={setWallBorders}
                  />
                </div>
              )}

              <div ref={assetLibraryCadMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 4 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 4 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 4 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <AssetLibraryPanel
                  libraryAssets={libraryAssets}
                  setLibraryAssets={setLibraryAssets}
                  generatedImages={sessionLibraryImages}
                  setGeneratedImages={setSessionLibraryImages}
                  currentProjectId={currentProjectId}
                  onImageDeleted={(remaining) => {
                    if (!isGenerating) setGeneratedImage(remaining[0] || null);
                  }}
                />
              </div>

              {/* Controls - Mobile */}
              <div className="glassmorphic-card rounded-3xl p-5 space-y-3">
                {/* Choose CAD zone - Specific Room only */}
                {selectedMode === 'specific-room' && (
                  <div ref={cadZonePickerMobileRef} className="rounded-2xl" style={{ position: showGuide && guideStep === 5 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    <div>
                      <span className="text-white/70 text-xs block mb-2">
                        Choose CAD zone you want to re-furnish<span className="text-red-500">*</span>
                      </span>

                      {isDetectingZones ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="text-center">
                            <Icon name="Loader" size={24} className="text-orange-500 mx-auto mb-2 animate-spin" />
                            <p className="text-white/70 text-xs">Detecting zones...</p>
                          </div>
                        </div>
                      ) : generatedZoneImages?.length > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                          {generatedZoneImages?.map((img, idx) => {
                            const zone = detectedZones[idx];
                            const roomLabel = zone?.label || `Zone ${idx + 1}`;
                            const isSelected = selectedZone === idx;
                            const isFailed = zone?.failed === true;
                            return (
                              <div key={idx} className="flex-shrink-0 w-32">
                                <button
                                  onClick={() => !isFailed && setSelectedZone(idx)}
                                  className={`w-full cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 relative ${
                                    isFailed
                                      ? 'border-red-500/30 opacity-50 cursor-not-allowed'
                                      : isSelected
                                        ? 'border-orange-500/60 shadow-[0_0_12px_rgba(254,131,56,0.15)]'
                                        : 'border-white/10 hover:border-orange-500/30'
                                  }`}
                                  disabled={isFailed}>
                                  <img src={img} alt={roomLabel} className="w-full h-24 object-cover" />
                                  {isFailed && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                      <Icon name="AlertTriangle" size={16} className="text-red-400" />
                                    </div>
                                  )}
                                </button>
                                <span className={`text-[10px] mt-1 block text-center transition-colors ${
                                  isFailed ? 'text-red-400' : isSelected ? 'text-orange-400' : 'text-white/60'
                                }`}>{roomLabel}{isFailed ? ' (failed)' : ''}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-white/50 text-xs">Upload CAD image to detect zones</p>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-white/20 my-2"></div>
                  </div>
                )}

                {/* Room section - Specific Room only */}
                {selectedMode === 'specific-room' && (
                  <RoomTypeDropdown
                    value={selectedRoomType}
                    onChange={(val) => {
                      setSelectedRoomType(val);
                      if (val === 'Custom') {
                        setTimeout(() => {
                          if (promptRef?.current) {
                            promptRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            promptRef.current.focus();
                          }
                        }, 80);
                      }
                    }}
                    label="Specify chosen CAD zone type"
                    mobile
                    showCustomOption
                  />
                )}

                {selectedMode === 'specific-room' ? (
                  <>
                    <div ref={angleViewCadMobileRef} className="space-y-3 rounded-xl" style={{ position: showGuide && guideStep === 6 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    {/* Common angle views toggle */}
                    <div>
                      <label className="text-white/70 text-[10px] mb-2 block">Common angle views</label>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>Frontal view</span>
                        <button
                          onClick={handleToggleAngleView}
                          className={`relative w-12 h-6 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                            commonAngleView === 'topdown' ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                        <span className={`text-[10px] transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>Top-down view</span>
                      </div>
                    </div>

                    {/* Angle view */}
                    <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} />
                    </div>

                    <div ref={modelCadMobileRef} className="space-y-3 rounded-xl" style={{ position: showGuide && guideStep === 7 && guideType === 'specific-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 7 && guideType === 'specific-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 7 && guideType === 'specific-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    {/* Model Selection - Mobile */}
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                      mobile
                    />

                    {/* Aspect Ratio + Resolution - full width stacked */}
                    <div className="space-y-3">
                      <div ref={aspectRatioMobileRef}>
                        <label className="text-white/70 text-xs mb-1 block">Aspect Ratio</label>
                        <div className="relative">
                          <button
                            onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                            className="w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all">
                            <div className="flex items-center gap-2">
                              <AspectRatioIcon
                                ratio={selectedImageSize}
                                size={14}
                                className="text-orange-500" />
                              <span className="text-white text-xs">{selectedImageSize}</span>
                            </div>
                            <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                          </button>
                          {isAspectRatioOpen && (
                            <div
                              className="absolute z-50 w-full bottom-full mb-1 rounded-xl border border-white/10 shadow-2xl max-h-60 overflow-y-auto"
                              style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                              {aspectRatios?.map((ratio) => (
                                <button
                                  key={ratio?.value}
                                  onClick={() => { setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 transition-all border-b border-white/5 last:border-b-0 ${
                                    selectedImageSize === ratio?.value ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                  }`}>
                                  <AspectRatioIcon
                                    ratio={ratio?.value}
                                    size={14}
                                    className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'} />
                                  <span className={`text-xs ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {ratio?.label}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Resolution Dropdown */}
                      <div ref={resolutionMobileRef}>
                        {!isFluxModel(selectedModel) && (
                        <>
                        <label className="text-white/70 text-xs mb-1 block">Resolution</label>
                        <div className="relative">
                          <button
                            onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                            className={`w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}>
                            <span className="text-white text-xs font-medium">{selectedResolution}</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-white/50 text-xs">
                                  {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                                </span>
                              <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                            </div>
                          </button>
                          {isResolutionOpen && (
                            <div
                              className="absolute z-50 w-full bottom-full mb-1 rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                              style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                              {RESOLUTION_OPTIONS.map((resKey) => {
                                const dims = getResolutionDimensions(resKey);
                                return (
                                  <button
                                    key={resKey}
                                    onClick={() => { setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                                      selectedResolution === resKey ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                    }`}>
                                    <span className={`text-xs ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                      {resKey}
                                    </span>
                                    <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                        {dims.width}×{dims.height}
                                      </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        </>
                        )}
                      </div>
                    </div>
                    </div>
                  </>
                ) : (
                  <>
                  {/* Whole room: Common angle views + Angle view + Aspect Ratio stacked */}
                  <div ref={angleViewCadMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === 5 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 5 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 5 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    <label className="text-white/70 text-[10px] mb-2 block">Common angle views</label>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[10px] transition-colors ${commonAngleView === 'frontal' ? 'text-white' : 'text-white/50'}`}>Frontal view</span>
                      <button
                        onClick={handleToggleAngleView}
                        className={`relative w-12 h-6 rounded-full transition-all duration-300 ${commonAngleView ? 'bg-orange-500' : 'bg-white/20'}`}>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                          commonAngleView === 'topdown' ? 'translate-x-6' : 'translate-x-0'}`} />
                      </button>
                      <span className={`text-[10px] transition-colors ${commonAngleView === 'topdown' ? 'text-white' : 'text-white/50'}`}>Top-down view</span>
                    </div>
                  </div>
                  <div ref={modelCadMobileRef} className="space-y-3 rounded-xl" style={{ position: showGuide && guideStep === 6 && guideType === 'whole-room' ? 'relative' : undefined, zIndex: showGuide && guideStep === 6 && guideType === 'whole-room' ? 201 : undefined, boxShadow: showGuide && guideStep === 6 && guideType === 'whole-room' && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                    <AngleViewDropdown value={selectedAngleView} onChange={handleAngleViewChange} mobile />
                    <ModelSelectionDropdown
                      value={selectedModel}
                      onChange={setSelectedModel}
                      mobile
                    />
                    <div ref={aspectRatioMobileRef}>
                      <label className="text-white/70 text-xs mb-1 block">Aspect Ratio</label>
                      <div className="relative">
                        <button
                          onClick={() => setIsAspectRatioOpen(!isAspectRatioOpen)}
                          className="w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center justify-between hover:border-orange-500/30 transition-all">
                          <div className="flex items-center gap-2">
                            <AspectRatioIcon
                              ratio={selectedImageSize}
                              size={14}
                              className="text-orange-500" />
                            <span className="text-white text-xs">{selectedImageSize}</span>
                          </div>
                          <Icon name={isAspectRatioOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                        </button>
                        {isAspectRatioOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-1 rounded-xl border border-white/10 shadow-2xl max-h-60 overflow-y-auto"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                            {aspectRatios?.map((ratio) => (
                              <button
                                key={ratio?.value}
                                onClick={() => { setSelectedImageSize(ratio?.value); setIsAspectRatioOpen(false); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 transition-all border-b border-white/5 last:border-b-0 ${
                                  selectedImageSize === ratio?.value ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                }`}>
                                <AspectRatioIcon
                                  ratio={ratio?.value}
                                  size={14}
                                  className={selectedImageSize === ratio?.value ? 'text-orange-500' : 'text-white/50'} />
                                <span className={`text-xs ${selectedImageSize === ratio?.value ? 'text-white font-medium' : 'text-white/70'}`}>
                                  {ratio?.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Resolution - full width */}
                  <div ref={resolutionMobileRef}>
                      {!isFluxModel(selectedModel) && (
                      <>
                      <label className="text-white/70 text-xs mb-1 block">Resolution</label>
                      <div className="relative">
                        <button
                          onClick={() => setIsResolutionOpen(!isResolutionOpen)}
                          className={`w-full glassmorphic-select rounded-xl px-3 h-[38px] bg-white/5 border border-white/10 flex items-center gap-3 justify-between hover:border-orange-500/30 transition-all`}>
                          <span className="text-white text-xs font-medium">{selectedResolution}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-white/50 text-xs">
                                {getResolutionDimensions(selectedResolution).width}×{getResolutionDimensions(selectedResolution).height}
                              </span>
                            <Icon name={isResolutionOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-white/50" />
                          </div>
                        </button>
                        {isResolutionOpen && (
                          <div
                            className="absolute z-50 w-full bottom-full mb-1 rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}>
                            {RESOLUTION_OPTIONS.map((resKey) => {
                              const dims = getResolutionDimensions(resKey);
                              return (
                                <button
                                  key={resKey}
                                  onClick={() => { setSelectedResolution(resKey); setIsResolutionOpen(false); }}
                                  className={`w-full flex items-center justify-between px-3 py-2.5 transition-all border-b border-white/5 last:border-b-0 ${
                                    selectedResolution === resKey ? 'bg-orange-500/20 hover:bg-orange-500/30' : 'hover:bg-white/5'
                                  }`}>
                                  <span className={`text-xs ${selectedResolution === resKey ? 'text-white font-medium' : 'text-white/70'}`}>
                                    {resKey}
                                  </span>
                                  <span className={`text-xs ${selectedResolution === resKey ? 'text-orange-400' : 'text-white/50'}`}>
                                      {dims.width}×{dims.height}
                                    </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      </>
                      )}
                  </div>
                  </>
                )}

                {/* Prompt */}
                <div ref={promptCadMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === promptGuideStep ? 'relative' : undefined, zIndex: showGuide && guideStep === promptGuideStep ? 201 : undefined, boxShadow: showGuide && guideStep === promptGuideStep && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                  <label className="text-white/70 text-[10px] mb-1 block">Prompt:</label>
                  {selectedRoomType === 'Custom' && (
                    <p className="text-orange-400/80 text-[11px] mb-1 flex items-center gap-1">✏️ Describe the room type you want in the prompt below</p>
                  )}
                  <input
                    type="text"
                    value={promptText}
                    onChange={(e) => setPromptText(e?.target?.value)}
                    placeholder={selectedRoomType === 'Custom' ? 'Describe the room type (e.g. yoga studio...)' : 'Additional request'}
                    className={`w-full glassmorphic-input rounded-xl px-3 py-2 text-sm text-white bg-white/5 focus:outline-none placeholder:text-white/30 ${selectedRoomType === 'Custom' ? 'border-2 border-orange-500/60 focus:border-orange-500' : 'border border-white/10 focus:border-orange-500/50'}`} />
                </div>

                {/* Generate Button */}
                <div ref={generateCadMobileRef} className="rounded-xl" style={{ position: showGuide && guideStep === generateGuideStep ? 'relative' : undefined, zIndex: showGuide && guideStep === generateGuideStep ? 201 : undefined, boxShadow: showGuide && guideStep === generateGuideStep && guideVisible ? glow : '0 0 0px transparent', transition: 'box-shadow 0.6s cubic-bezier(0.4,0,0.2,1)' }}>
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full glassmorphic-button rounded-xl py-2.5 text-sm text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Icon name={isGenerating ? 'Loader' : 'Sparkles'} size={16} className={isGenerating ? 'animate-spin' : ''} />
                  {isGenerating ? 'Generating...' : 'Generate Image'}
                </button>
                {isGenerating && (
                  <button
                    onClick={handleCancelGeneration}
                    className="w-full glassmorphic-button rounded-xl py-2 text-sm text-white/70 font-medium flex items-center justify-center gap-2 hover:text-white transition-colors"
                  >
                    <Icon name="X" size={16} />
                    Cancel
                  </button>
                )}
                </div>

                <p className="text-white/50 text-[10px] text-center">Your request will cost {getModelCreditCost(selectedModel)} credit{getModelCreditCost(selectedModel) !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* Virtual Tool Guide Overlay */}
      {showGuide && (
        <>
          <div className="fixed inset-0 z-[200]" onClick={closeGuide}>
            <div
              className="absolute inset-0 transition-opacity duration-600 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{ background: 'rgba(0, 0, 0, 0.30)', opacity: guideVisible ? 1 : 0 }}
            />
          </div>

          {/* Tooltip bubble */}
          <div
            className="fixed z-[9999] w-[280px] md:w-[300px] pointer-events-none"
            style={(() => {
              const wholeRoomDesktopRefs = [modeSelectorDesktopRef.current, cadZoneTypeDesktopRef.current, uploadCadDesktopRef.current, reorgOptionsDesktopRef.current, angleViewCadDesktopRef.current, modelCadDesktopRef.current, promptCadDesktopRef.current, generateCadDesktopRef.current];
              const specificRoomDesktopRefs = [modeSelectorDesktopRef.current, cadZoneTypeDesktopRef.current, uploadCadDesktopRef.current, assetLibraryCadDesktopRef.current, cadZonePickerDesktopRef.current, angleViewCadDesktopRef.current, modelCadDesktopRef.current, promptCadDesktopRef.current, generateCadDesktopRef.current];
              const wholeRoomMobileRefs = [modeSelectorMobileRef.current, cadZoneTypeMobileRef.current, uploadCadMobileRef.current, reorgOptionsMobileRef.current, angleViewCadMobileRef.current, modelCadMobileRef.current, promptCadMobileRef.current, generateCadMobileRef.current];
              const specificRoomMobileRefs = [modeSelectorMobileRef.current, cadZoneTypeMobileRef.current, uploadCadMobileRef.current, assetLibraryCadMobileRef.current, cadZonePickerMobileRef.current, angleViewCadMobileRef.current, modelCadMobileRef.current, promptCadMobileRef.current, generateCadMobileRef.current];
              const isDesktop = window.innerWidth >= 768;
              const desktopRefs = guideType === 'whole-room' ? wholeRoomDesktopRefs : specificRoomDesktopRefs;
              const mobileRefs = guideType === 'whole-room' ? wholeRoomMobileRefs : specificRoomMobileRefs;
              const ref = isDesktop ? desktopRefs[guideStep - 1] : mobileRefs[guideStep - 1];
              const bubbleWidth = isDesktop ? 300 : 280;
              if (!ref) return { top: '50%', left: '50%' };
              const rect = ref.getBoundingClientRect();
              if (!isDesktop || guideStep === 1) {
                return {
                  top: rect.bottom + 12,
                  left: Math.max(12, Math.min(window.innerWidth - bubbleWidth - 12, rect.left + rect.width / 2 - bubbleWidth / 2)),
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(0px)' : 'translateY(-8px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              if (guideStep >= 5) {
                // Steps 5+: right column → bubble to the LEFT
                return {
                  top: rect.top + rect.height / 2,
                  left: rect.left - bubbleWidth - 32,
                  opacity: guideVisible ? 1 : 0,
                  transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(24px)',
                  transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                };
              }
              // Steps 2-4: all left column → bubble to the RIGHT
              return {
                top: rect.top + rect.height / 2,
                left: rect.right + 32,
                opacity: guideVisible ? 1 : 0,
                transform: guideVisible ? 'translateY(-50%) translateX(0px)' : 'translateY(-50%) translateX(-24px)',
                transition: 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              };
            })()}
          >
            <div className="pointer-events-auto">
              {/* Arrow indicator */}
              {(window.innerWidth < 768 || guideStep === 1) ? (
                // Mobile or step 1: up arrow (bubble is below element)
                <>
                  <div
                    className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderBottom: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-0 h-0"
                    style={{ borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : guideStep >= 5 ? (
                // Steps 5+: right column → right-pointing arrow (bubble is to the left)
                <>
                  <div
                    className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -right-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              ) : (
                // Steps 2-4: left column → left-pointing arrow (bubble is to the right)
                <>
                  <div
                    className="absolute top-1/2 -left-2 -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderRight: '10px solid rgba(254, 131, 56, 0.3)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                  <div
                    className="absolute top-1/2 -left-[6px] -translate-y-1/2 w-0 h-0"
                    style={{ borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderRight: '9px solid rgb(24, 24, 24)', opacity: guideVisible ? 1 : 0, transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.15s' }}
                  />
                </>
              )}

              <div
                className="rounded-2xl p-5 text-center"
                style={{ background: 'rgb(24, 24, 24)', border: '1px solid rgba(254, 131, 56, 0.3)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 20px rgba(254, 131, 56, 0.1)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 mb-2"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s' }}
                >
                  <Icon
                    name={
                      guideStep === 1 ? 'Layers' :
                      guideStep === 2 ? 'LayoutGrid' :
                      guideStep === 3 ? 'ImagePlus' :
                      guideStep === 4 ? (guideType === 'whole-room' ? 'ArrowLeftRight' : 'FolderOpen') :
                      guideStep === angleViewGuideStep ? 'Eye' :
                      guideStep === modelGuideStep ? 'Cpu' :
                      guideStep === promptGuideStep ? 'MessageSquare' :
                      'Sparkles'
                    }
                    size={18}
                    className="text-white/50"
                  />
                  <span className="text-sm font-semibold text-white">Step {guideStep}</span>
                </div>
                <p
                  className="text-sm text-white/80 leading-relaxed"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s' }}
                >
                  {guideType === 'whole-room' ? (
                    guideStep === 1 ? (
                      <>Select <span className="text-white font-medium italic">Whole Room Variation</span> mode to rearrange all furniture zones across your entire CAD floor plan.</>
                    ) : guideStep === 2 ? (
                      <>Select the <span className="text-white font-medium italic">CAD zone type</span> — either a 3D visualization (Rendered Plan) or flat, black-and-white technical drawing (Architectural Plan).</>
                    ) : guideStep === 3 ? (
                      <>Upload your <span className="text-white font-medium italic">CAD floor plan</span> image based on the type you chose in Step 2. The AI uses it as the base to generate a new spatial layout.</>
                    ) : guideStep === 4 ? (
                      <>Use <span className="text-white font-medium italic">Zone Swap</span> to swap furniture zones between rooms, and <span className="text-white font-medium italic">Wall Borders</span> to keep or remove wall outlines in the result.</>
                    ) : guideStep === 5 ? (
                      <>Set the <span className="text-white font-medium italic">angle view</span> — choose a frontal or top-down perspective to control how the AI frames the generated layout.</>
                    ) : guideStep === 6 ? (
                      <>Select your <span className="text-white font-medium italic">AI model</span>, aspect ratio, and resolution to define the output quality and image dimensions.</>
                    ) : guideStep === 7 ? (
                      <>Add an optional <span className="text-white font-medium italic">prompt</span> to guide the AI — describe specific materials, styles, or any spatial preferences you want applied.</>
                    ) : (
                      <>Hit <span className="text-white font-medium italic">Generate Image</span> to let the AI redesign the full floor plan based on your settings.</>
                    )
                  ) : (
                    guideStep === 1 ? (
                      <>Select <span className="text-white font-medium italic">Specific Room Only</span> mode to focus the redesign on one room zone, leaving the rest of the floor plan untouched.</>
                    ) : guideStep === 2 ? (
                      <>Select the <span className="text-white font-medium italic">CAD zone type</span> — either a 3D visualization (Rendered Plan) or flat, black-and-white technical drawing (Architectural Plan).</>
                    ) : guideStep === 3 ? (
                      <>Upload your <span className="text-white font-medium italic">CAD floor plan</span> image based on the type you chose in Step 2. Room zones will be automatically detected after upload.</>
                    ) : guideStep === 4 ? (
                      <>Your saved and generated images are stored in the <span className="text-white font-medium italic">Asset Library</span>. Drag and drop them into the upload zone to reuse them as references.</>
                    ) : guideStep === 5 ? (
                      <>The AI will automatically <span className="text-white font-medium italic">detect room zones</span> from your uploaded plan. Click a zone to select the specific room you want to redesign.</>
                    ) : guideStep === 6 ? (
                      <>Specify the <span className="text-white font-medium italic">room type</span> for the selected zone and set the angle view to control how the space will be rendered.</>
                    ) : guideStep === 7 ? (
                      <>Choose your <span className="text-white font-medium italic">AI model</span> and resolution to set the output quality for the selected room zone.</>
                    ) : guideStep === 8 ? (
                      <>Add an optional <span className="text-white font-medium italic">prompt</span> to describe specific styles, materials, or design preferences for the selected room.</>
                    ) : (
                      <>Hit <span className="text-white font-medium italic">Generate Image</span> to redesign just the selected zone while keeping the rest of the floor plan intact.</>
                    )
                  )}
                </p>
                {guideStep === 2 && (
                  <div
                    className="flex gap-2 mt-3"
                    style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.25s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.25s' }}
                  >
                    <div className="flex-1 text-center">
                      <img
                        src="/assets/images/cad-room-rendered.png"
                        alt="Rendered Plan"
                        className="w-full h-[72px] object-cover rounded-lg"
                        style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                      <span className="text-white/50 text-[10px] mt-1 block">Rendered Plan</span>
                    </div>
                    <div className="flex-1 text-center">
                      <img
                        src="/assets/images/cad-tech-drawing.png"
                        alt="Architectural Plan"
                        className="w-full h-[72px] object-cover rounded-lg"
                        style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                      />
                      <span className="text-white/50 text-[10px] mt-1 block">Architectural Plan</span>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => guideStep < totalGuideSteps ? nextGuideStep() : closeGuide()}
                  className="mt-4 px-5 py-2 glassmorphic-button rounded-xl text-white text-xs font-medium hover:scale-[1.02]"
                  style={{ opacity: guideVisible ? 1 : 0, transform: guideVisible ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s' }}
                >
                  {guideStep < totalGuideSteps ? 'Next' : 'Got it'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)'
          }
        }}
      />
    </>
  );
};

export default CADSpacePlanner;
